import '../utils/systemjs.polyfill.js';

import { EventType, MsgName } from './constants';
import {
  decodeDmOperationsClickCb,
  DEFAULT_FPS,
  getCCResourceUrl,
  getQueryObj,
  hideMouseAuto,
  IS_DEBUG,
  protocolMap,
} from './utils';

/**
 * 加载CC
 */
const loadCCResource = async () => {
  const resourceUrl = getCCResourceUrl(Boolean(Number(getQueryObj().isEngineTune)));

  window.System.addImportMap({
    imports: { cc: `${resourceUrl}/cocos-js/cc.js` },
  });

  const { Application } = await window.System.import(`${resourceUrl}/application.js`);
  const application = new Application();
  const engine = await window.System.import(`${resourceUrl}/cocos-js/cc.js`);

  // 根据引擎初始化application
  await application.init(engine);

  // 场景激活时，通知SDK
  window.cc?.director?.once('director_after_scene_launch', () => {
    sendMessageToSDK(EventType.CC_SCENE_ACTIVE);
  });

  const option = {
    showFPS: IS_DEBUG,
    settingsPath: `${resourceUrl}/src/settings.json`,
    assetOptions: {
      server: `${resourceUrl}/`,
      remoteBundles: ['main', 'resources', 'internal', 'dev_resource', 'font_apple', 'font_other_system'],
    },
    overrideSettings: {
      screen: {
        frameRate: DEFAULT_FPS, // 锁定帧数
      },
      plugins: { jsList: [] }, // 重写插件配置, mathewchen 给的配置有一项无法加载
    },
  };
  // application.start里会执行game.init方法，详见 ./application.js#start
  await application.start(option);

  return application;
};

/**
 * 向弹幕SDK发送消息, 接收逻辑在 src/index.ts 中
 */
const sendMessageToSDK = (() => {
  let id = 0;
  return (name: EventType, value: unknown = null) => {
    if (window.parent === window) return;
    id += 1;
    window.parent.postMessage({ id, name, value }, '*');
  };
})();

/**
 * 接收弹幕SDK发送的消息
 */
const onMessageFromSDK = ({ data }) => {
  if (!window.cc?.game?.inited) return;
  const { name, value } = data;

  switch (name) {
    case EventType.CC_RUN:
      window.cc.game.run(() => {
        sendMessageToSDK(EventType.CC_RUN);
      });
      break;
    case EventType.CC_PAUSE:
      window.cc.game.pause();
      break;
    case EventType.CC_RESUME:
      if (!window.cc.game._paused) {
        sendMessageToSDK(EventType.CALLBACK, { id: data.id });
      } else {
        window.cc.game.once('game_on_resume', () => {
          sendMessageToSDK(EventType.CALLBACK, { id: data.id });
        });
        window.cc.game.resume();
      }
      break;
    case EventType.CC_MSG:
      sendMessageToCC(value.name, value.data);
      break;
    case EventType.FPS: {
      const { deltaTime } = window.cc.game;
      sendMessageToSDK(EventType.CALLBACK, {
        id: data.id,
        data: Math.min(1 / deltaTime, DEFAULT_FPS),
      });
      break;
    }
    case EventType.HEART_BEAT:
      sendMessageToSDK(EventType.CALLBACK, { id: data.id, callbackFrom: EventType.HEART_BEAT });
      break;
    case EventType.RESIZE:
      window.cc.screen.windowSize = new window.cc.math.Size(
        value.width * window.devicePixelRatio,
        value.height * window.devicePixelRatio,
      );
      break;
  }
};

/**
 * 监听尺寸变化，更新cc的渲染尺寸
 */
const resizeObserver = () => {
  if (typeof ResizeObserver !== 'function') return;

  return new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        const { width, height } = entry.contentRect;
        window.cc.screen.windowSize = new window.cc.math.Size(
          width * window.devicePixelRatio,
          height * window.devicePixelRatio,
        );
      }
    }
  });
};

/**
 * 检测 webgl 上下文
 */
const checkWebglContext = () => {
  const canvas = document.querySelector('#GameCanvas') as HTMLCanvasElement;
  if (!canvas) {
    sendMessageToSDK(EventType.CC_MSG, { name: EventType.WEBGL_CONTEXT_LOST });
    return;
  }
  canvas.addEventListener(
    'webglcontextlost',
    (event) => {
      event.preventDefault();
      sendMessageToSDK(EventType.CC_MSG, { name: EventType.WEBGL_CONTEXT_LOST });
    },
    false,
  );
};

/**
 * frame 中 向CC发送信息
 */
const sendMessageToCC = (name: MsgName, value: unknown) => {
  if (!window._MDW_?.sendMessage) return;
  const ccProtocol: any = protocolMap(name);
  if (!ccProtocol) return;

  const data = ccProtocol.encode(value).finish();
  const msgName = ccProtocol.originName ?? name;
  window._MDW_.sendMessage(
    window._MDW_.proto.native.CocosNativeMessage.encode({
      name: msgName,
      data,
      timestamp: Date.now(),
    }).finish(),
  );
};

/**
 * frame 接收CC发来的消息，eg、config变化、点击事件
 */
const onMessageFromCC = (msg) => {
  const { name, data } = window._MDW_.proto.native.CocosNativeMessage.decode(msg);

  switch (name) {
    case 'DanmakuConfigChange':
      sendMessageToSDK(EventType.CC_MSG, { name, data: window._MDW_.proto.cocos.DanmakuConfigChange.decode(data) });
      break;
    case 'ClickConsumeResult':
      // eslint-disable-next-line no-case-declarations
      const decodeData = window._MDW_.proto.cocos.CCClickConsumeResult.decode(data);
      // 处理特殊点击事件
      if (decodeData.operationType === window._MDW_.proto.cocos.CCClickOperationType.CC_CLICK_OPERATION_TYPE_OUTER) {
        decodeDmOperationsClickCb(decodeData.operations);
      }
      sendMessageToSDK(EventType.CC_MSG, { name, data: decodeData });
      break;
    case 'DanmakuReport':
      sendMessageToSDK(EventType.CC_MSG, { name, data: window._MDW_.proto.cocos.DanmakuReports.decode(data) });
      break;
    case 'ErrorReport':
      sendMessageToSDK(EventType.CC_MSG, { name, data: window._MDW_.proto.cocos.ErrorReports.decode(data) });
      break;
    /** 引擎侧事件，可代表消息通道已建立，做一些初始化的逻辑 */
    case 'DanmakuWorldEvent':
      sendMessageToSDK(EventType.CC_MSG, { name, data: window._MDW_.proto.cocos.DanmakuWorldEvent.decode(data) });
      break;
    case 'TABInfoRequests':
      sendMessageToSDK(EventType.CC_MSG, { name, data: window._MDW_.proto.cocos.TABInfoRequests.decode(data) });
      break;
    default:
      sendMessageToSDK(EventType.CC_MSG, { name });
      break;
  }
};

/**
 * pick from options
 * @param list 参数名列表
 * @param options 原始数据
 */
const pickOptions = (list: string[], options) => {
  const ret = {};
  list.forEach((k) => (ret[k] = options[k]));
  return ret;
};

/** 键盘事件代理字段 */
// eslint-disable-next-line prettier/prettier
const KEYBOARD_EVENT_KEY = ['ctrlKey', 'shiftKey', 'altKey', 'metaKey', 'key', 'keyCode', 'isTrusted', 'cancelable', 'code', 'composed', 'repeat'];
/** 鼠标事件代理字段 */
// eslint-disable-next-line prettier/prettier
const MOUSE_EVENT_KEY = ['screenX', 'screenY', 'clientX', 'clientY', 'ctrlKey', 'shiftKey', 'altKey', 'metaKey', 'button', 'buttons' ];
/** 指针事件代理字段 */
// eslint-disable-next-line prettier/prettier
const POINTER_EVENT_KEY = [...MOUSE_EVENT_KEY, 'pointerId', 'width', 'height', 'pressure', 'tangentialPressure', 'tiltX', 'tiltY', 'twist', 'pointerType', 'isPrimary'];

/**
 * 处理页面事件，将事件传给SDK(父页面)
 */
const eventHandler = (ev: MouseEvent | PointerEvent | KeyboardEvent) => {
  const { name } = ev.constructor;

  let options = null;
  if (ev instanceof KeyboardEvent) {
    options = pickOptions(KEYBOARD_EVENT_KEY, ev);
  } else if (ev instanceof PointerEvent) {
    options = pickOptions(POINTER_EVENT_KEY, ev);
  } else if (ev instanceof MouseEvent) {
    options = pickOptions(MOUSE_EVENT_KEY, ev);
  }

  if (options) sendMessageToSDK(EventType.HTML_EVENT, { name, type: ev.type, options });
};

/**
 * 代理页面事件
 */
const proxyHtmlEvent = () => {
  const dom = document.body;
  dom.addEventListener('mouseup', eventHandler);
  dom.addEventListener('mousedown', eventHandler);
  dom.addEventListener('mousemove', eventHandler);
  // dom.addEventListener('mouseleave', eventHandler); // 2.0引擎该事件触发不正常，先下线

  dom.addEventListener('click', eventHandler);
  dom.addEventListener('dblclick', eventHandler);
  dom.addEventListener('contextmenu', eventHandler);

  dom.addEventListener('keydown', eventHandler);
  dom.addEventListener('keypress', eventHandler);
  dom.addEventListener('keyup', eventHandler);

  // 鼠标自动隐藏（hideMouseAuto函数内已有截流）
  dom.addEventListener('mousemove', hideMouseAuto());
};

async function init() {
  sendMessageToSDK(EventType.FRAME_LOADED);
  window._MDW_ = window._MDW_ || {};
  // 加载CC资源
  try {
    await loadCCResource();
    // debug模式下，开启cocos侧日志。枚举值由引擎侧提供 0: 所有日志；1: debug日志；2: info日志；3: warn日志；4: error日志
    localStorage.setItem('cc_log_level', IS_DEBUG ? '0' : '');
  } catch (error) {
    sendMessageToSDK(EventType.CC_LOAD_FAILED);
    throw error;
  }

  // 接受CC发来的消息
  window._MDW_.onMessage = onMessageFromCC;

  // 接受SDK发来的消息
  window.addEventListener('message', onMessageFromSDK);

  // 通知SDK已经加载完成
  sendMessageToSDK(EventType.CC_LOADED);
  checkWebglContext();

  // 运行CC引擎
  window.cc.game.run(() => {
    resizeObserver()?.observe(document.body);
    sendMessageToSDK(EventType.CC_RUN);
  });

  // 监听页面事件
  proxyHtmlEvent();
}

init();
